import { Link, getRouteApi, useRouter } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { BUILTIN_ICONS, ICON_LIMITS, glyphSvg, packFileNames, type MapIcon } from '@realmery/realm-core';
import examplePackUrl from '../../../../samples/icons/asteria-extras.zip?url';
import { IconSwatch } from '../canvas/IconPicker.tsx';
import { useIconUrls } from '../canvas/pins.tsx';
import { readIconFiles, suggestedPackName, type IconFile, type IconReading } from '../canvas/iconImport.ts';
import { forgetSavedPack, readSavedPack, savePack, savedPacks } from '../canvas/savedPacks.ts';
import { RealmClientError, realms } from '../db/client.ts';
import { realmFile } from '../db/protocol.ts';
import { zip } from '../export/zip.ts';
import { saveBytes } from '../files.ts';
import { settings } from '../settings.ts';

/**
 * The Icon Library (docs/PARITY-STEP-4-PLAN.md section 10), as the desktop's
 * `IconLibraryWindow`: the built-in icons, then each pack imported into the realm; importing
 * pictures, single-colour SVGs, a folder or a zipped pack (with what couldn't be used, and
 * why); renaming and deleting an icon, with how many pins and categories use it; exporting a
 * pack to share; and saving a pack in this browser for any realm to add (decision 7).
 */

const route = getRouteApi('/_app/realm/$realmKey/atlas/icons');
const BUILT_IN = 'Built-in';
/** What icons are shown on in the library (`IconPicker.SWATCH`). */
const SWATCH = '#3c3350';

const message = (e: unknown, fallback: string) => (e instanceof RealmClientError ? e.message : fallback);
const plural = (n: number, one: string, many: string) => `${n.toLocaleString('en')} ${n === 1 ? one : many}`;

type Picked = { readonly kind: 'builtin'; readonly key: string } | { readonly kind: 'imported'; readonly id: string } | null;

export function IconLibraryPage() {
  const icons = route.useLoaderData() as MapIcon[];
  const { realmKey } = route.useParams();
  const file = realmFile(realmKey);
  const router = useRouter();
  const pictureUrl = useIconUrls();
  const byId = useMemo(() => new Map(icons.map((i) => [i.id, i])), [icons]);
  const packs = useMemo(() => [...new Set(icons.map((i) => i.pack))], [icons]);
  const [pack, setPack] = useState(BUILT_IN);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Picked>(null);
  const [usage, setUsage] = useState<number | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [importing, setImporting] = useState<{ files: IconFile[]; folder: string | null } | null>(null);
  const [report, setReport] = useState<{ pack: string; reading: IconReading } | null>(null);
  const [saved, setSaved] = useState<string[] | null>(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const shownPack = pack === BUILT_IN || packs.includes(pack) ? pack : BUILT_IN;
  const q = query.trim().toLowerCase();
  const builtin = shownPack === BUILT_IN ? BUILTIN_ICONS.filter((i) => q === '' || i.name.toLowerCase().includes(q) || i.group.toLowerCase().includes(q)) : [];
  const imported = shownPack === BUILT_IN ? [] : icons.filter((i) => i.pack === shownPack && (q === '' || i.name.toLowerCase().includes(q)));
  const pickedIcon = picked?.kind === 'imported' ? byId.get(picked.id) : undefined;
  const pickedBuiltin = picked?.kind === 'builtin' ? BUILTIN_ICONS.find((i) => i.key === picked.key) : undefined;

  useEffect(() => {
    setUsage(null);
    if (!pickedIcon) return;
    let live = true;
    void realms.iconUsage(file, pickedIcon.id).then((n) => live && setUsage(n));
    return () => {
      live = false;
    };
  }, [file, pickedIcon]);

  async function run(work: () => Promise<unknown>, fallback: string): Promise<boolean> {
    setError('');
    try {
      await work();
      await router.invalidate();
      return true;
    } catch (e) {
      setError(message(e, fallback));
      return false;
    }
  }

  /** Imports what was read, and says what came in and what was skipped (`IconImportFlow.report`). */
  async function bring(packName: string, reading: IconReading) {
    if (reading.icons.length > 0 && !(await run(() => realms.importIcons(file, reading.icons), 'The icons could not be imported.'))) return;
    setReport({ pack: packName, reading });
    if (reading.icons.length > 0) setPack(reading.icons[0]!.pack);
  }

  async function chosen(list: FileList | null, folder: boolean) {
    if (!list || list.length === 0) return;
    const files: IconFile[] = [];
    for (const f of Array.from(list)) files.push({ name: f.name, data: new Uint8Array(await f.arrayBuffer()) });
    const folderName = folder ? ((list[0] as File & { webkitRelativePath?: string }).webkitRelativePath?.split('/')[0] ?? null) : null;
    setReport(null);
    setImporting({ files, folder: folderName });
  }

  async function addZip(name: string, bytes: Uint8Array) {
    setReport(null);
    await bring(name, await readIconFiles([{ name: `${name}.zip`, data: bytes }], name));
  }

  /** A pack as a zip: pictures as they are, glyphs as small SVGs (`IconPackWriter.zip`). */
  function packZip(name: string): Uint8Array<ArrayBuffer> {
    const members = icons.filter((i) => i.pack === name);
    const names = packFileNames(members);
    return zip(
      new Map(
        members.map((icon, k) => {
          if (icon.kind !== 'GLYPH') return [names[k]!, icon.data];
          const stored = new TextDecoder().decode(icon.data);
          const marked = stored.length > 2 && stored[1] === '|';
          return [names[k]!, new TextEncoder().encode(glyphSvg({ path: marked ? stored.slice(2) : stored, evenOdd: marked && stored[0] === 'E' }))];
        }),
      ),
    );
  }

  return (
    <article className="document icon-library-page">
      <p className="eyebrow">
        <Link to="/realm/$realmKey/atlas" params={{ realmKey }}>
          ← Atlas
        </Link>
      </p>
      <h1>Icon Library</h1>
      <p className="lead">The icons pins, tokens and categories can show: Realmery's own, and your own in named packs that travel with the realm.</p>
      <div className="actions icon-library-actions">
        <label className="button" title="Import PNG, JPG, WebP, or SVG pictures - or a zipped pack - as icons">
          Import Icons…
          <input type="file" multiple hidden accept=".png,.jpg,.jpeg,.webp,.svg,.zip,image/png,image/jpeg,image/webp,image/svg+xml,application/zip" onChange={(e) => void chosen(e.currentTarget.files, false).then(() => (e.target.value = ''))} />
        </label>
        <label className="button" title="Import every image in a folder as one pack">
          Import Folder…
          <input type="file" multiple hidden {...{ webkitdirectory: '' }} onChange={(e) => void chosen(e.currentTarget.files, true).then(() => (e.target.value = ''))} />
        </label>
        <button type="button" title="Add a pack you saved for all realms in this browser" onClick={() => void savedPacks().then(setSaved)}>
          Add Saved Pack…
        </button>
        <button
          type="button"
          title="Import a small example pack to try"
          onClick={() =>
            void fetch(examplePackUrl)
              .then((r) => r.arrayBuffer())
              .then((b) => addZip('Asteria Extras', new Uint8Array(b)))
              .catch(() => setError("The example pack couldn't be read."))
          }
        >
          Try Example Pack
        </button>
      </div>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      {importing && (
        <PackForm
          files={importing.files}
          suggested={suggestedPackName(importing.files, importing.folder)}
          onCancel={() => setImporting(null)}
          onImport={async (name) => {
            const reading = await readIconFiles(importing.files, name);
            setImporting(null);
            await bring(name, reading);
          }}
        />
      )}
      {report && <ImportReport pack={report.pack} reading={report.reading} onClose={() => setReport(null)} />}
      {saved && (
        <section className="panel" aria-label="Saved packs">
          <h3>Add a saved pack to this realm</h3>
          {saved.length === 0 ? (
            <p className="meta">No packs are saved in this browser yet. Pick a pack below and choose Save Pack for All Realms.</p>
          ) : (
            <ul className="icon-saved-packs">
              {saved.map((name) => (
                <li key={name}>
                  <span>{name}</span>
                  <button
                    type="button"
                    onClick={() =>
                      void readSavedPack(name).then((bytes) => {
                        setSaved(null);
                        if (bytes) void addZip(name, bytes);
                        else setError("That saved pack couldn't be read.");
                      })
                    }
                  >
                    Add
                  </button>
                  <button type="button" className="quiet" title="Forget this pack in this browser" onClick={() => void forgetSavedPack(name).then(() => savedPacks().then(setSaved))}>
                    Forget
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button type="button" onClick={() => setSaved(null)}>
            Close
          </button>
        </section>
      )}
      <div className="icon-library">
        <nav className="icon-packs" aria-label="Packs">
          <h2>Packs</h2>
          <ul>
            {[BUILT_IN, ...packs].map((p) => (
              <li key={p}>
                <button
                  type="button"
                  className={p === shownPack ? 'quiet selected' : 'quiet'}
                  aria-pressed={p === shownPack}
                  onClick={() => {
                    setPack(p);
                    setPicked(null);
                  }}
                >
                  {p} <span className="meta">{p === BUILT_IN ? BUILTIN_ICONS.length : icons.filter((i) => i.pack === p).length}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
        <section className="icon-grid-section" aria-label={shownPack}>
          <label>
            <span className="visually-hidden">Search icons</span>
            <input type="search" value={query} placeholder="Search" title="Show only the icons whose names contain this" onChange={(e) => setQuery(e.currentTarget.value)} />
          </label>
          <div className="icon-grid" role="radiogroup" aria-label="Icons">
            {builtin.map((i) => (
              <IconButton key={i.key} name={i.name} value={i.ref} picked={picked?.kind === 'builtin' && picked.key === i.key} onPick={() => setPicked({ kind: 'builtin', key: i.key })} byId={byId} pictureUrl={pictureUrl} />
            ))}
            {imported.map((i) => (
              <IconButton key={i.id} name={i.name} value={`img:${i.id}`} picked={picked?.kind === 'imported' && picked.id === i.id} onPick={() => setPicked({ kind: 'imported', id: i.id })} byId={byId} pictureUrl={pictureUrl} />
            ))}
            {builtin.length === 0 && imported.length === 0 && <p className="meta">Nothing here matches.</p>}
          </div>
          <p className="meta icon-detail" role="status">
            {pickedBuiltin
              ? `${pickedBuiltin.name}: a built-in icon, in the ${pickedBuiltin.group} group. Built-in icons can't be changed.`
              : pickedIcon
                ? `${pickedIcon.name}: ${pickedIcon.kind === 'GLYPH' ? 'a single-colour icon, shown in its category’s colour' : 'a picture'}${usage === null ? '' : `, used by ${plural(usage, 'pin or category', 'pins and categories')}`}.`
                : 'Pick an icon to rename or delete it.'}
          </p>
          {pickedIcon && (
            <div className="actions">
              <button type="button" title="Rename the picked icon" onClick={() => setRenaming(true)}>
                Rename…
              </button>
              <button
                type="button"
                className="quiet"
                title="Delete the picked icon - pins using it go back to their category's icon"
                onClick={() => (settings()['general.confirmDeletes'] ? setDeleting(true) : void run(() => realms.deleteIcon(file, pickedIcon.id), 'The icon could not be deleted.').then(() => setPicked(null)))}
              >
                Delete…
              </button>
            </div>
          )}
          {renaming && pickedIcon && (
            <RenameForm
              name={pickedIcon.name}
              onCancel={() => setRenaming(false)}
              onSave={(name) => run(() => realms.renameIcon(file, pickedIcon.id, name), 'The icon could not be renamed.').then((ok) => ok && setRenaming(false))}
            />
          )}
          {deleting && pickedIcon && (
            <Confirm
              label={`Delete ${pickedIcon.name}`}
              text={`Delete “${pickedIcon.name}”? ${usage === 0 || usage === null ? 'Nothing uses it.' : `${plural(usage, 'pin or category uses it', 'pins and categories use it')}. Pins go back to their category's icon, and categories to a plain dot.`}`}
              button="Delete icon"
              onCancel={() => setDeleting(false)}
              onConfirm={() =>
                run(() => realms.deleteIcon(file, pickedIcon.id), 'The icon could not be deleted.').then(() => {
                  setDeleting(false);
                  setPicked(null);
                })
              }
            />
          )}
          {shownPack !== BUILT_IN && (
            <div className="actions icon-pack-actions">
              <button type="button" title="Save this pack as a file to share" onClick={() => saveBytes(packZip(shownPack), `${shownPack.replace(/[^\p{L}\p{N} _-]/gu, '').trim() || 'icons'}.zip`, 'application/zip')}>
                Export Pack…
              </button>
              <button
                type="button"
                title="Keep a copy of this pack in this browser, so any realm here can add it"
                onClick={() => void savePack(shownPack, packZip(shownPack)).then((ok) => setNotice(ok ? `“${shownPack}” is saved in this browser: any realm here can add it with Add Saved Pack.` : "This browser couldn't save the pack."))}
              >
                Save Pack for All Realms
              </button>
            </div>
          )}
        </section>
      </div>
    </article>
  );
}

function IconButton({ name, value, picked, onPick, byId, pictureUrl }: { name: string; value: string; picked: boolean; onPick: () => void; byId: ReadonlyMap<string, MapIcon>; pictureUrl: Parameters<typeof IconSwatch>[0]['pictureUrl'] }) {
  return (
    <button type="button" role="radio" aria-checked={picked} aria-label={name} title={name} className="icon-choice icon-library-choice" onClick={onPick}>
      <IconSwatch value={value} color={SWATCH} icons={byId} pictureUrl={pictureUrl} size={40} />
      <span>{name}</span>
    </button>
  );
}

function PackForm({ files, suggested, onImport, onCancel }: { files: readonly IconFile[]; suggested: string; onImport: (pack: string) => Promise<void>; onCancel: () => void }) {
  const [name, setName] = useState(suggested);
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="panel"
      aria-label="Import icons"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        void onImport(name.trim() || 'Imported').finally(() => setBusy(false));
      }}
    >
      <p>{plural(files.length, 'file', 'files')} chosen. Which pack should these go in?</p>
      <label>
        Pack
        <input value={name} maxLength={ICON_LIMITS.pack} autoFocus onChange={(e) => setName(e.currentTarget.value)} />
      </label>
      <div className="actions">
        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Importing…' : 'Import'}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function ImportReport({ pack, reading, onClose }: { pack: string; reading: IconReading; onClose: () => void }) {
  const { icons, problems } = reading;
  return (
    <section className={icons.length === 0 ? 'panel notice-error' : 'panel'} role="status" aria-label="Import result">
      <p>
        <strong>{icons.length === 0 ? 'Nothing could be imported.' : `Imported ${plural(icons.length, 'icon', 'icons')} into “${pack}”.`}</strong>
      </p>
      {problems.length === 0 ? (
        <p>They're in the icon picker and the Icon Library.</p>
      ) : (
        <>
          <p>Skipped:</p>
          <ul>
            {problems.slice(0, 12).map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
          {problems.length > 12 && <p>…and {(problems.length - 12).toLocaleString('en')} more.</p>}
        </>
      )}
      <button type="button" onClick={onClose}>
        OK
      </button>
    </section>
  );
}

function RenameForm({ name: initial, onSave, onCancel }: { name: string; onSave: (name: string) => Promise<unknown>; onCancel: () => void }) {
  const [name, setName] = useState(initial);
  return (
    <form
      className="panel"
      aria-label="Rename icon"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void onSave(name);
      }}
    >
      <label>
        Name
        <input value={name} required maxLength={ICON_LIMITS.name} autoFocus onChange={(e) => setName(e.currentTarget.value)} />
      </label>
      <div className="actions">
        <button type="submit" className="primary">
          Rename
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function Confirm({ label, text, button, onConfirm, onCancel }: { label: string; text: ReactNode; button: string; onConfirm: () => Promise<unknown>; onCancel: () => void }) {
  return (
    <div className="panel confirm" role="alertdialog" aria-label={label}>
      <p>{text}</p>
      <div className="actions">
        <button type="button" className="danger" onClick={() => void onConfirm()}>
          {button}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

